import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { CustomFieldsCard } from "@/components/CustomFieldsCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Mail,
  Phone,
  Building2,
  DollarSign,
  Sparkles,
  Send,
  MessageSquare,
  Edit2,
  Save,
  Clock,
  RefreshCw,
  CheckCheck,
  ArrowDownLeft,
  ArrowUpRight,
  FileText,
  Camera,
  ExternalLink,
  ShoppingCart,
  FileDown,
  Package,
  MapPin,
  Eye
} from "lucide-react";

const STATUSES = [
  { value: "new", label: "New", color: "bg-blue-100 text-blue-700" },
  { value: "contacted", label: "Contacted", color: "bg-yellow-100 text-yellow-700" },
  { value: "qualified", label: "Qualified", color: "bg-purple-100 text-purple-700" },
  { value: "proposal", label: "Proposal", color: "bg-indigo-100 text-indigo-700" },
  { value: "won", label: "Won", color: "bg-green-100 text-green-700" },
  { value: "lost", label: "Lost", color: "bg-red-100 text-red-700" },
];

const getStatusColor = (status) => {
  const found = STATUSES.find(s => s.value === status);
  return found?.color || "bg-slate-100 text-slate-700";
};

export default function LeadDetailPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const messagesEndRef = useRef(null);
  
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [relatedOrders, setRelatedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderActionModal, setShowOrderActionModal] = useState(false);
  const [showFullOrderView, setShowFullOrderView] = useState(false);
  const [orderZoom, setOrderZoom] = useState(80);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [activeTab, setActiveTab] = useState("messages");
  
  // Messaging state
  const [messageType, setMessageType] = useState("sms");
  const [messageText, setMessageText] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [sending, setSending] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [newNote, setNewNote] = useState("");

  // Auto-refresh for new messages
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchLeadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  // Auto-refresh messages every 10 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchActivities();
    }, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, autoRefresh]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchLeadData = async () => {
    try {
      const [leadRes, activitiesRes, ordersRes] = await Promise.all([
        axios.get(`${API}/leads/${leadId}`),
        axios.get(`${API}/leads/${leadId}/activities`),
        axios.get(`${API}/leads/${leadId}/orders`).catch(() => ({ data: [] }))
      ]);
      setLead(leadRes.data);
      setEditData(leadRes.data);
      setActivities(activitiesRes.data);
      setRelatedOrders(ordersRes.data);
    } catch (error) {
      toast.error("Failed to fetch lead details");
      navigate("/leads");
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/leads/${leadId}/activities`);
      setActivities(response.data);
    } catch (error) {
      // Error handled silently — non-critical operation
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/leads/${leadId}`, editData);
      setLead(editData);
      setEditing(false);
      toast.success("Lead updated successfully");
    } catch (error) {
      toast.error("Failed to update lead");
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/leads/${leadId}`, { status: newStatus });
      setLead({ ...lead, status: newStatus });
      fetchActivities();
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleAIScore = async () => {
    try {
      toast.loading("AI is analyzing the lead...");
      const response = await axios.post(`${API}/ai/score-lead`, { lead_id: leadId });
      setLead({ ...lead, score: response.data.score });
      toast.dismiss();
      toast.success(`Lead scored: ${response.data.score}/100`);
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to score lead");
    }
  };

  const handleAIDraft = async () => {
    setDraftLoading(true);
    try {
      const response = await axios.post(`${API}/ai/draft-response`, {
        lead_id: leadId,
        context: messageType === "email" ? "Follow-up email - be professional and helpful" : "Follow-up SMS - keep it brief and friendly"
      });
      setMessageText(response.data.draft);
      if (messageType === "email") {
        setEmailSubject("Following up on your inquiry");
      }
      toast.success("AI draft generated");
    } catch (error) {
      toast.error("Failed to generate draft");
    } finally {
      setDraftLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    if (messageType === "email" && !emailSubject.trim()) {
      toast.error("Please enter an email subject");
      return;
    }
    
    setSending(true);
    try {
      await axios.post(`${API}/messaging/send`, {
        lead_id: leadId,
        message_type: messageType,
        subject: messageType === "email" ? emailSubject : undefined,
        body: messageText
      });
      
      setMessageText("");
      setEmailSubject("");
      fetchActivities();
      toast.success(messageType === "sms" ? "SMS sent!" : "Email sent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await axios.post(`${API}/leads/${leadId}/activities`, {
        activity_type: "note",
        content: newNote
      });
      setNewNote("");
      fetchActivities();
      toast.success("Note added");
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  // Filter activities for messages only
  const messages = activities.filter(a => 
    ["sms", "sms_inbound", "email"].includes(a.activity_type)
  ).reverse(); // Show oldest first for chat view

  const notes = activities.filter(a => 
    ["note", "status_change", "created"].includes(a.activity_type)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!lead) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {lead.first_name} {lead.last_name}
            </h1>
            <p className="text-slate-600">{lead.company || lead.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={getStatusColor(lead.status)}>{lead.status}</Badge>
          <Button variant="outline" onClick={handleAIScore} data-testid="ai-score-btn">
            <Sparkles className="w-4 h-4 mr-2" />
            AI Score {lead.score > 0 && `(${lead.score})`}
          </Button>
          <Button onClick={() => navigate(`/estimates/new?lead_id=${lead.lead_id}`)} data-testid="convert-estimate-btn">
            <FileText className="w-4 h-4 mr-2" />
            Create Estimate
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Messages */}
        <div className="lg:col-span-2">
          <Card className="h-[600px] flex flex-col" data-testid="messaging-card">
            <CardHeader className="border-b flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Conversation
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={fetchActivities}
                    data-testid="refresh-messages"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <span className="text-xs text-slate-500">
                    {autoRefresh ? "Auto-refresh on" : "Auto-refresh off"}
                  </span>
                </div>
              </div>
            </CardHeader>
            
            {/* Messages Area */}
            <CardContent className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-500">
                  <MessageSquare className="w-12 h-12 mb-3 text-slate-300" />
                  <p>No messages yet</p>
                  <p className="text-sm">Send an SMS or email to start the conversation</p>
                </div>
              ) : (
                messages.map((msg, index) => (
                  <MessageBubble key={msg.activity_id || index} message={msg} lead={lead} />
                ))
              )}
              <div ref={messagesEndRef} />
            </CardContent>

            {/* Message Input */}
            <div className="border-t p-4 flex-shrink-0 bg-white">
              {/* Message Type Toggle */}
              <div className="flex gap-2 mb-3">
                <Button
                  variant={messageType === "sms" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setMessageType("sms")}
                  className="flex-1"
                  data-testid="msg-type-sms"
                >
                  <Phone className="w-4 h-4 mr-1" />
                  SMS
                </Button>
                <Button
                  variant={messageType === "email" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setMessageType("email")}
                  className="flex-1"
                  data-testid="msg-type-email"
                >
                  <Mail className="w-4 h-4 mr-1" />
                  Email
                </Button>
              </div>

              {/* Email Subject (only for email) */}
              {messageType === "email" && (
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Email subject..."
                  className="mb-2"
                  data-testid="email-subject"
                />
              )}

              {/* Message Input */}
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder={messageType === "sms" ? "Type your SMS..." : "Type your email..."}
                    rows={2}
                    className="resize-none pr-20"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey && messageType === "sms") {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    data-testid="message-input"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-2"
                    onClick={handleAIDraft}
                    disabled={draftLoading}
                    data-testid="ai-draft-btn"
                  >
                    <Sparkles className={`w-4 h-4 ${draftLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
                <Button 
                  onClick={handleSendMessage}
                  disabled={sending || !messageText.trim()}
                  className="self-end"
                  data-testid="send-btn"
                >
                  {sending ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
              
              {messageType === "sms" && lead.phone && (
                <p className="text-xs text-slate-500 mt-2">
                  Sending to: {lead.phone}
                </p>
              )}
              {messageType === "email" && lead.email && (
                <p className="text-xs text-slate-500 mt-2">
                  Sending to: {lead.email}
                </p>
              )}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Lead Info Card */}
          <Card data-testid="lead-info-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base">Contact Info</CardTitle>
              {editing ? (
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSave}>
                    <Save className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                  <Edit2 className="w-3 h-3" />
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {editing ? (
                <div className="space-y-3">
                  <Input
                    value={editData.first_name}
                    onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                    placeholder="First name"
                  />
                  <Input
                    value={editData.last_name}
                    onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
                    placeholder="Last name"
                  />
                  <Input
                    value={editData.email}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    placeholder="Email"
                  />
                  <Input
                    value={editData.phone || ""}
                    onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    placeholder="Phone"
                  />
                  <Input
                    value={editData.company || ""}
                    onChange={(e) => setEditData({ ...editData, company: e.target.value })}
                    placeholder="Company"
                  />
                  <Input
                    type="number"
                    value={editData.value || 0}
                    onChange={(e) => setEditData({ ...editData, value: parseFloat(e.target.value) || 0 })}
                    placeholder="Value ($)"
                  />
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="truncate">{lead.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{lead.phone || "No phone"}</span>
                  </div>
                  {lead.company && (
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      <span>{lead.company}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <DollarSign className="w-4 h-4 text-slate-400" />
                    <span>${(lead.value || 0).toLocaleString()}</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Status Card */}
          <Card data-testid="status-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Status</CardTitle>
            </CardHeader>
            <CardContent>
              <Select value={lead.status} onValueChange={handleStatusChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      <span className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${status.color.split(' ')[0]}`}></span>
                        {status.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="mt-3 text-xs text-slate-500 space-y-1">
                <p>Source: <span className="capitalize">{lead.source}</span></p>
                <p>Created: {new Date(lead.created_at).toLocaleDateString()}</p>
                {lead.last_contacted && (
                  <p>Last contact: {new Date(lead.last_contacted).toLocaleDateString()}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Custom Fields */}
          <CustomFieldsCard entityType="lead" entityId={lead.lead_id} />

          {/* Notes Card */}
          <Card data-testid="notes-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-3">
                <Input
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note..."
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddNote();
                  }}
                  data-testid="add-note-input"
                />
                <Button size="sm" onClick={handleAddNote} disabled={!newNote.trim()}>
                  Add
                </Button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {notes.length > 0 ? (
                  notes.map((note, index) => (
                    <div key={note.activity_id || index} className="text-sm bg-slate-50 rounded p-2">
                      <p className="text-slate-700">{note.content}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(note.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-400">No notes yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Related Orders Card */}
          {relatedOrders.length > 0 && (
            <Card data-testid="related-orders-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" />
                  Orders ({relatedOrders.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {relatedOrders.map((order) => (
                    <div
                      key={order.order_id}
                      className="flex items-center justify-between text-sm bg-slate-50 rounded p-2 cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => {
                        setSelectedOrder(order);
                        setShowOrderActionModal(true);
                      }}
                      data-testid={`order-${order.order_id}`}
                    >
                      <div className="flex-1">
                        <p className="font-medium text-slate-700 capitalize">
                          {order.order_type?.replace(/_/g, " ") || "Order"}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(order.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-slate-800">
                          ${(order.order_value || 0).toLocaleString()}
                        </p>
                        <Badge variant="outline" className="text-xs capitalize">
                          {order.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Photos Card */}
          {((lead.photos && lead.photos.length > 0) || (lead.attachments && lead.attachments.length > 0)) && (
            <Card data-testid="photos-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Photos ({(lead.photos?.length || 0) + (lead.attachments?.length || 0)})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {lead.photos?.map((photoUrl, index) => (
                    <a 
                      key={`photo-${index}-${photoUrl?.slice(-10)}`}
                      href={photoUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="relative group"
                    >
                      <img 
                        src={photoUrl} 
                        alt={`Photo ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border hover:border-indigo-400 transition-colors"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%2394a3b8" font-size="12">No image</text></svg>';
                        }}
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <ExternalLink className="w-5 h-5 text-white" />
                      </div>
                    </a>
                  ))}
                  {lead.attachments?.map((attachment, index) => (
                    <a 
                      key={`attach-${index}-${attachment.name || index}`}
                      href={attachment.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="relative group"
                    >
                      <img 
                        src={attachment.url} 
                        alt={attachment.name || `Attachment ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border hover:border-indigo-400 transition-colors"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%2394a3b8" font-size="12">No image</text></svg>';
                        }}
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <ExternalLink className="w-5 h-5 text-white" />
                      </div>
                      {attachment.name && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 rounded-b-lg truncate">
                          {attachment.name}
                        </div>
                      )}
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* View Order Action Modal */}
      <Dialog open={showOrderActionModal} onOpenChange={setShowOrderActionModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">How would you like to view this order?</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            {selectedOrder?.pdf_url ? (
              <a
                href={selectedOrder.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowOrderActionModal(false)}
                className="flex items-center gap-3 p-4 border rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                data-testid="download-pdf-option"
              >
                <FileDown className="w-6 h-6 text-orange-600 shrink-0" />
                <div>
                  <p className="font-medium text-slate-900">Download PDF</p>
                  <p className="text-xs text-slate-500">Download the supplier-ready PDF</p>
                </div>
              </a>
            ) : (
              <div className="flex items-center gap-3 p-4 border rounded-lg bg-slate-50 opacity-60">
                <FileDown className="w-6 h-6 text-slate-400 shrink-0" />
                <div>
                  <p className="font-medium text-slate-500">Download PDF</p>
                  <p className="text-xs text-slate-400">No PDF available for this order</p>
                </div>
              </div>
            )}
            <button
              onClick={() => {
                setShowOrderActionModal(false);
                setShowFullOrderView(true);
              }}
              className="flex items-center gap-3 p-4 border rounded-lg hover:bg-slate-50 transition-colors text-left"
              data-testid="view-full-option"
            >
              <Eye className="w-6 h-6 text-indigo-600 shrink-0" />
              <div>
                <p className="font-medium text-slate-900">View Full Order</p>
                <p className="text-xs text-slate-500">View the complete order details</p>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full Order View Modal */}
      <Dialog open={showFullOrderView} onOpenChange={(open) => { setShowFullOrderView(open); if (!open) setOrderZoom(80); }}>
        <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-hidden p-0 flex flex-col">
          <div className="flex items-center justify-between px-4 py-2 bg-slate-100 border-b shrink-0">
            <span className="text-xs text-slate-500 font-medium">Zoom: {orderZoom}%</span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setOrderZoom(Math.max(50, orderZoom - 10))}>-</Button>
              <input type="range" min="50" max="120" value={orderZoom} onChange={(e) => setOrderZoom(Number(e.target.value))} className="w-32 h-1 accent-indigo-600" data-testid="zoom-slider" />
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setOrderZoom(Math.min(120, orderZoom + 10))}>+</Button>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setOrderZoom(80)}>Reset</Button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" style={{ fontSize: `${orderZoom}%` }}>
          {selectedOrder && (
            <div className="bg-white" data-testid="full-order-view">
              <div className="bg-slate-800 text-white p-6">
                <h2 className="text-xl font-bold">Order Specification</h2>
                <div className="flex items-center justify-between mt-2">
                  <div className="text-sm text-slate-300 space-y-1">
                    <p>Order #{selectedOrder.order_id?.slice(-8).toUpperCase()}</p>
                    {selectedOrder.quote_id && <p>Quote: {selectedOrder.quote_id}</p>}
                    <p>{new Date(selectedOrder.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">${(selectedOrder.order_value || 0).toLocaleString()}</p>
                    <p className="text-sm text-slate-300 capitalize">{selectedOrder.order_type?.replace(/_/g, " ")}</p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-6">
                {/* Quote Details */}
                {(selectedOrder.door_style || selectedOrder.finish || selectedOrder.cabinet_count || selectedOrder.delivery_option || selectedOrder.payment_option) && (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Quote Details</h3>
                    <div className="bg-indigo-50 rounded-lg p-4 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      {selectedOrder.door_style && (<div><span className="text-slate-500 block text-xs">Door Style</span><span className="font-medium">{selectedOrder.door_style}</span></div>)}
                      {selectedOrder.finish && (<div><span className="text-slate-500 block text-xs">Finish</span><span className="font-medium">{selectedOrder.finish}</span></div>)}
                      {selectedOrder.cabinet_count && (<div><span className="text-slate-500 block text-xs">Cabinets</span><span className="font-medium">{selectedOrder.cabinet_count}</span></div>)}
                      {selectedOrder.delivery_option && (<div><span className="text-slate-500 block text-xs">Delivery</span><span className="font-medium capitalize">{selectedOrder.delivery_option?.replace(/_/g, " ")}</span></div>)}
                      {selectedOrder.payment_option && (<div><span className="text-slate-500 block text-xs">Payment</span><span className="font-medium capitalize">{selectedOrder.payment_option?.replace(/_/g, " ")}</span></div>)}
                    </div>
                  </div>
                )}
                <div>
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Customer Information</h3>
                  <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                    <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /><span className="font-medium">{selectedOrder.customer_name}</span></div>
                    {selectedOrder.customer_email && (<div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-slate-400" /><span>{selectedOrder.customer_email}</span></div>)}
                    {selectedOrder.customer_phone && (<div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-slate-400" /><span>{selectedOrder.customer_phone}</span></div>)}
                    {selectedOrder.customer_address && (<div className="flex items-center gap-2 text-sm"><MapPin className="w-4 h-4 text-slate-400" /><span>{selectedOrder.customer_address}</span></div>)}
                  </div>
                </div>
                {selectedOrder.items && selectedOrder.items.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Order Items</h3>
                    <table className="w-full border-collapse">
                      <thead><tr className="bg-slate-100">
                        <th className="text-left text-xs font-semibold text-slate-600 p-2 border">Product Code</th>
                        <th className="text-left text-xs font-semibold text-slate-600 p-2 border">Item</th>
                        <th className="text-left text-xs font-semibold text-slate-600 p-2 border">Dimensions</th>
                        <th className="text-center text-xs font-semibold text-slate-600 p-2 border">Qty</th>
                        <th className="text-right text-xs font-semibold text-slate-600 p-2 border">Unit Price</th>
                        <th className="text-right text-xs font-semibold text-slate-600 p-2 border">Total</th>
                      </tr></thead>
                      <tbody>
                        {selectedOrder.items.map((item, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="p-2 border text-xs font-mono text-slate-500">{item.item_code || item.product_sku || item.details?.item_code || item.details?.code || "—"}</td>
                            <td className="p-2 border text-sm font-medium">{item.name}</td>
                            <td className="p-2 border text-xs text-slate-600">{item.details?.width && item.details?.height ? `${item.details.width}"W x ${item.details.height}"H${item.details.depth ? ` x ${item.details.depth}"D` : ""}` : "—"}</td>
                            <td className="p-2 border text-sm text-center">{item.quantity}</td>
                            <td className="p-2 border text-sm text-right">${(item.price || 0).toLocaleString()}</td>
                            <td className="p-2 border text-sm text-right font-medium">${((item.price || 0) * (item.quantity || 0)).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {/* Pricing Breakdown */}
                {selectedOrder.pricing_breakdown ? (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Pricing Breakdown</h3>
                    <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                      {selectedOrder.pricing_breakdown.subtotal != null && (<div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span>${selectedOrder.pricing_breakdown.subtotal.toLocaleString()}</span></div>)}
                      {selectedOrder.pricing_breakdown.delivery_cost > 0 && (<div className="flex justify-between"><span className="text-slate-600">Delivery</span><span>${selectedOrder.pricing_breakdown.delivery_cost.toLocaleString()}</span></div>)}
                      {selectedOrder.pricing_breakdown.assembly_cost > 0 && (<div className="flex justify-between"><span className="text-slate-600">Assembly</span><span>${selectedOrder.pricing_breakdown.assembly_cost.toLocaleString()}</span></div>)}
                      {selectedOrder.pricing_breakdown.total_cost != null && (<div className="flex justify-between pt-2 border-t font-medium"><span>Total</span><span>${selectedOrder.pricing_breakdown.total_cost.toLocaleString()}</span></div>)}
                      {selectedOrder.pricing_breakdown.discount_amount > 0 && (<div className="flex justify-between text-green-600"><span>Discount (20%)</span><span>-${selectedOrder.pricing_breakdown.discount_amount.toLocaleString()}</span></div>)}
                      {selectedOrder.pricing_breakdown.discount_amount > 0 && selectedOrder.pricing_breakdown.total_cost != null && (
                        <div className="flex justify-between pt-2 border-t font-bold text-base"><span>Final Total</span><span className="text-green-700">${((selectedOrder.pricing_breakdown.total_cost || 0) - (selectedOrder.pricing_breakdown.discount_amount || 0)).toLocaleString()}</span></div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-lg p-4 flex justify-between font-semibold"><span>Total</span><span>${(selectedOrder.order_value || 0).toLocaleString()}</span></div>
                )}
                {/* SMS Sent */}
                {selectedOrder.sms_sent?.sent && (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">SMS Notification</h3>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-sm font-medium text-green-700 mb-2">
                        <Phone className="w-4 h-4" />
                        Sent to {selectedOrder.sms_sent.phone}
                        {selectedOrder.sms_sent.timestamp && (
                          <span className="text-xs text-green-500 ml-auto">{new Date(selectedOrder.sms_sent.timestamp).toLocaleString()}</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedOrder.sms_sent.message}</p>
                    </div>
                  </div>
                )}
                {selectedOrder.notes && (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Notes</h3>
                    <div className="bg-slate-50 rounded-lg p-4"><p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedOrder.notes}</p></div>
                  </div>
                )}
                {selectedOrder.photos && selectedOrder.photos.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Photos</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {selectedOrder.photos.map((url, idx) => (
                        <a key={idx} href={url} target="_blank" rel="noopener noreferrer"><img src={url} alt={`Photo ${idx+1}`} className="w-full h-28 object-cover rounded-lg border" onError={(e) => { e.target.style.display='none'; }}/></a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          </div>
          {/* Fixed bottom action bar */}
          {selectedOrder && (
          <div className="shrink-0 border-t bg-white px-4 py-3 flex items-center justify-between">
            <span className="text-xs text-slate-400">Source: {selectedOrder.source}</span>
            <div className="flex gap-2">
              {selectedOrder.pdf_url && (
                <a href={selectedOrder.pdf_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm"><FileDown className="w-3 h-3 mr-1" />Download PDF</Button>
                </a>
              )}
              <Button variant="outline" size="sm" onClick={() => setShowFullOrderView(false)}>Close</Button>
            </div>
          </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MessageBubble({ message, lead }) {
  const isInbound = message.activity_type === "sms_inbound";
  const isEmail = message.activity_type === "email";
  const isSMS = message.activity_type === "sms";
  
  // Parse email content to get subject if present
  let displayContent = message.content;
  let subject = null;
  if (isEmail && message.content.startsWith("Subject:")) {
    const lines = message.content.split("\n");
    subject = lines[0].replace("Subject:", "").trim();
    displayContent = lines.slice(2).join("\n").trim();
  }

  return (
    <div className={`flex ${isInbound ? "justify-start" : "justify-end"}`}>
      <div 
        className={`max-w-[80%] rounded-2xl px-4 py-2 ${
          isInbound 
            ? "bg-white border border-slate-200 rounded-bl-md" 
            : isEmail 
              ? "bg-blue-600 text-white rounded-br-md"
              : "bg-indigo-600 text-white rounded-br-md"
        }`}
      >
        {/* Message Type Indicator */}
        <div className={`flex items-center gap-1 text-xs mb-1 ${isInbound ? "text-slate-500" : "text-white/70"}`}>
          {isInbound ? (
            <>
              <ArrowDownLeft className="w-3 h-3" />
              <span>{lead.first_name} replied</span>
            </>
          ) : isEmail ? (
            <>
              <Mail className="w-3 h-3" />
              <span>Email sent</span>
            </>
          ) : (
            <>
              <ArrowUpRight className="w-3 h-3" />
              <span>SMS sent</span>
            </>
          )}
        </div>

        {/* Email Subject */}
        {subject && (
          <p className={`text-xs font-medium mb-1 ${isInbound ? "text-slate-600" : "text-white/80"}`}>
            {subject}
          </p>
        )}

        {/* Message Content */}
        <p className={`text-sm whitespace-pre-wrap ${isInbound ? "text-slate-800" : "text-white"}`}>
          {displayContent}
        </p>

        {/* Timestamp */}
        <div className={`flex items-center justify-end gap-1 mt-1 text-xs ${isInbound ? "text-slate-400" : "text-white/60"}`}>
          <span>{new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          {!isInbound && <CheckCheck className="w-3 h-3" />}
        </div>
      </div>
    </div>
  );
}
